import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ServerError } from 'app/Public/ServerError';
import { useTenant } from 'app/TenantValidator';
import {
  profileAtom,
  useBootIntercom,
  useEnforceCompanyConfiguration,
  useEnforceProfile,
} from 'init';
import { useAtomValue } from 'jotai';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isDefined, isNonNullish, isNullish } from 'remeda';
import { useLoadCompany } from 'shared/hooks/use-load-company';
import { useLoadPrivateConstants } from 'shared/hooks/use-load-private-constants';
import { useLoadFeatureFlags } from 'shared/utils/feature-flags';
import type { StoreState } from 'types/storeTypes';
import { LoadingScreen } from './LoadingScreen';

interface LoggedLoaderProps extends React.PropsWithChildren<{}> {}

const InnerPrivateLoader = ({ children }: LoggedLoaderProps) => {
  const history = useHistory();
  const tenant = useTenant();

  // These are the 4 requests that must complete before the app can be considered loaded and children rendered
  const { profile, profileError } = useEnforceProfile();
  const { companyConfigurationError, isCompanyConfigurationReady } =
    useEnforceCompanyConfiguration();
  const { privateConstantsError, isLoadingPrivateConstants } = useLoadPrivateConstants();
  const { isCompanyLoading: isCompanyV2Loading, companyError: companyV2Error } = useLoadCompany();
  const profileFromAtom = useAtomValue(profileAtom);
  const privateConstantsFromState = useSelector((state: StoreState) => state.app.constants);

  useLoadFeatureFlags();
  useBootIntercom();

  useEffect(() => {
    if (
      isDefined(profile) &&
      !profile.hasAcceptedGc &&
      !window.location.pathname.includes('terms')
    ) {
      history.push('/terms-and-conditions');
    } else if (
      isDefined(tenant) &&
      !tenant.onboarding.isComplete &&
      !window.location.pathname.includes('onboarding')
    ) {
      history.push('/onboarding');
    }
  }, [profile?.hasAcceptedGc, tenant?.onboarding?.isComplete]);

  const isAppLoading =
    !isCompanyConfigurationReady ||
    isCompanyV2Loading ||
    isLoadingPrivateConstants ||
    isNullish(privateConstantsFromState) ||
    isNullish(profileFromAtom) ||
    isNullish(tenant);

  const hasLoadingFailed =
    isNonNullish(companyConfigurationError) ||
    isNonNullish(companyV2Error) ||
    isNonNullish(profileError) ||
    isNonNullish(privateConstantsError);

  // In the future it would be good to identify how the loading has failed; if 403 because
  // user is no longer part of the company handle it separately
  if (hasLoadingFailed) return <ServerError />;

  if (isAppLoading) return <LoadingScreen />;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
      {children}
    </motion.div>
  );
};

export const PrivateLoader = withAuthenticationRequired(InnerPrivateLoader);
